import NotionAvatar from "react-notion-avatar";
import Avvvatars from "avvvatars-react";
import type { AvatarConfig } from "react-notion-avatar";

interface AvatarDisplayProps {
  email: string;
  avatarConfig?: Record<string, any> | null;
  /** OAuth/Clerk profile image URL — shown when the user hasn't built a custom avatar */
  imageUrl?: string | null;
  size?: number;
  className?: string;
  onClick?: () => void;
  showEditOverlay?: boolean;
}

function isValidNotionConfig(config: unknown): config is AvatarConfig {
  if (!config || typeof config !== "object") return false;
  const c = config as Record<string, unknown>;
  return (
    typeof c.face === "number" &&
    typeof c.eye === "number" &&
    typeof c.eyebrow === "number" &&
    typeof c.mouth === "number" &&
    typeof c.nose === "number" &&
    typeof c.hair === "number"
  );
}

/** Returns true when the URL points to a generic/placeholder avatar rather than a real profile photo. */
function isDefaultAvatar(imageUrl: string): boolean {
  if (!imageUrl) return true;

  // Gravatar generic fallbacks
  if (imageUrl.includes("gravatar.com")) {
    const genericPatterns = ["d=mp", "d=identicon", "d=monsterid", "d=wavatar", "d=retro", "d=robohash", "d=404"];
    if (genericPatterns.some((p) => imageUrl.includes(p))) return true;
  }

  // Clerk-generated placeholder avatars
  if (imageUrl.includes("clerk.dev") || imageUrl.includes("clerk.com")) {
    if (imageUrl.toLowerCase().includes("avatar")) return true;
  }

  // Other common placeholder services
  const placeholderHosts = ["ui-avatars.com", "api.dicebear.com", "boringavatars.com", "avatars.dicebear.com"];
  if (placeholderHosts.some((h) => imageUrl.includes(h))) return true;

  return false;
}

export default function AvatarDisplay({
  email,
  avatarConfig,
  imageUrl,
  size = 40,
  className = "",
  onClick,
  showEditOverlay = false,
}: AvatarDisplayProps) {
  const hasNotionAvatar = isValidNotionConfig(avatarConfig);
  const hasRealPhoto = !!imageUrl && !isDefaultAvatar(imageUrl);

  // Priority: custom NotionAvatar > real OAuth photo > Avvvatars fallback
  let content: React.ReactNode;
  if (hasNotionAvatar) {
    content = (
      <NotionAvatar
        config={avatarConfig as AvatarConfig}
        shape="circle"
        className="w-full h-full"
        bgColor="#f1f5f9"
      />
    );
  } else if (hasRealPhoto) {
    content = (
      <img
        src={imageUrl!}
        alt="Profile picture"
        className="w-full h-full object-cover"
        referrerPolicy="no-referrer"
      />
    );
  } else {
    content = <Avvvatars value={email} style="character" size={size} />;
  }

  return (
    <div
      className={`relative flex-shrink-0 overflow-hidden rounded-full bg-white ${onClick ? "cursor-pointer" : ""} ${className}`}
      style={{ width: size, height: size }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
    >
      {content}

      {showEditOverlay && (
        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 transition-colors hover:bg-black/30 group">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100"
          >
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            <path d="m15 5 4 4" />
          </svg>
        </div>
      )}
    </div>
  );
}
